import { useEffect, useState } from 'react';
import { fetchVoterElections } from '@/api/voters';
import { VotingRecordDots, type VotingRecordEntry } from '@/components/VotingRecordDots';

/**
 * VoterVotingRecord — lazily fetches a voter's recent voting record and
 * renders it as the G/P/S dot grid. Meant to live inside a Leaflet popup,
 * which react-leaflet mounts only when the popup opens, so the fetch fires
 * on open (not for every marker). Results are cached per voter.
 */
export function VoterVotingRecord({ voterId }: { voterId: number }) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [record, setRecord] = useState<VotingRecordEntry[]>([]);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    fetchVoterElections(voterId)
      .then((r) => {
        if (!alive) return;
        setRecord(r.elections);
        setStatus('ok');
      })
      .catch(() => {
        if (alive) setStatus('error');
      });
    return () => {
      alive = false;
    };
  }, [voterId]);

  if (status === 'loading') {
    return <p className="text-[11px] text-slate-400 italic">loading voting record…</p>;
  }
  if (status === 'error') {
    return <p className="text-[11px] text-slate-400 italic">voting record unavailable</p>;
  }

  return (
    <div>
      <VotingRecordDots record={record} />
      <p className="mt-1 text-[10px] text-slate-400 leading-tight">
        ● voted · ○ missed — G general · P primary · S special
      </p>
    </div>
  );
}
